// shared-lib/lighting/islandPendantRelay.js
//
// Re-hang the pendants a TOOL hung over an island when the island changes.
//
// Pendants are placed by a kitchen layout's apply, `style_kitchen` and the
// Design Center's pendant pick, each from the island as it stood then. A
// cabinet-run edit — a redraw, a re-solve, a delete — changes the island
// under them and touched no light, so the pendants of an island's earlier
// shape stayed where its counter used to be (eight over a drawn island whose
// rule now gives two; the user, 2026-11-03, asked for them to be re-placed on
// a redraw).
//
// After a run edit, the tool pendants that hung over that run's island are
// taken down and the same rule (`pendantPlacement`) hangs the row again over
// the island as it now stands, each copy keeping the first one's fixture,
// stem, height and provenance. A pendant hung by hand (no tool `addedBy`) is
// the user's and stays put; an island with no tool pendant gets none.
//
// Pure. No I/O.

const {
  resolvePendantAnchors,
  planPendantPositions,
  pendantObstacles,
  isIslandCounter,
} = require('../kitchen-assembly/styling/pendantPlacement');
const {
  KITCHEN_LIGHT_SOURCES,
} = require('../kitchen-assembly/styling/kitchenLightingRegion');
const { planOutline } = require('../geometry/objectFootprint');
const { isPointInsideOrOnPolygon } = require('../geometry/pointInPolygon');

/** The tools whose pendants follow the island they were hung over. */
const RELAID_SOURCES = new Set([...KITCHEN_LIGHT_SOURCES, 'design_center']);

/** How far off a counter's outline a pendant still hangs over it. */
const OVER_TOL_M = 0.15;

const isToolPendant = (o) =>
  o?.lightingSubtype === 'pendant' &&
  RELAID_SOURCES.has(o.addedBy) &&
  !!o.center;

const isCounter = (o) =>
  o?.type === 'countertop' ||
  o?.type === 'corner-fill-countertop' ||
  o?.category?.countertop === true;

/** The run's island counter pieces in `objects` — bend pieces included. */
const islandPieces = (objects, runId) =>
  (objects || []).filter(
    (o) => o?.runId === runId && isCounter(o) && isIslandCounter(o)
  );

/**
 * A test "does this pendant hang over the run's island" for `objects` — the
 * island's counter pieces, bends included, with `OVER_TOL_M` round them — or
 * null when the run has no island counter in `objects`.
 */
function overRunIsland(objects, runId) {
  const outlines = islandPieces(objects, runId)
    .map(planOutline)
    .filter((p) => p?.length >= 3);
  if (!outlines.length) return null;
  return (o) =>
    outlines.some((poly) =>
      isPointInsideOrOnPolygon(
        { x: o.center.x, z: o.center.z },
        poly,
        OVER_TOL_M
      )
    );
}

/**
 * Every pendant hanging over the run's island — whoever hung it, a tool or a
 * hand. What the run panel asks before it offers to hang a row there.
 */
function pendantsOverRunIsland(objects, runId) {
  const over = runId ? overRunIsland(objects, runId) : null;
  if (!over) return [];
  return (objects || []).filter(
    (o) => o?.lightingSubtype === 'pendant' && !!o.center && over(o)
  );
}

/**
 * `next` (the room's objects after a run edit) with that run's island's tool
 * pendants re-hung. `prior` is the room's objects before the edit — where the
 * island stood, so the pendants that hung over it are found even when the edit
 * moved or removed it. Returns `next` itself when nothing hung over it.
 *
 * @param {Object[]} prior
 * @param {Object[]} next
 * @param {string} runId
 * @returns {Object[]}
 */
function relayIslandPendants(prior, next, runId) {
  if (!runId) return next;
  const over = overRunIsland(prior, runId);
  if (!over) return next;
  const stale = (next || []).filter((o) => isToolPendant(o) && over(o));
  if (!stale.length) return next;

  const staleSet = new Set(stale);
  const rest = next.filter((o) => !staleSet.has(o));
  const template = stale[0];
  const width = template.dimensions?.x || 0.3;
  const bottomY = template.center.y - (template.dimensions?.y || 0) / 2;
  const obstacles = pendantObstacles(rest, bottomY, Infinity);
  const positions = resolvePendantAnchors(rest)
    .filter((a) => a.runId === runId)
    .flatMap((a) =>
      planPendantPositions(a, { pendantWidthM: width, obstacles })
    );
  const base = String(template.id).replace(/_\d+$/, '');
  const hung = positions.map((p, i) => ({
    ...template,
    id: `${base}_${runId}_${i}`,
    center: { x: p.x, y: template.center.y, z: p.z },
  }));
  return rest.concat(hung);
}

module.exports = {
  relayIslandPendants,
  pendantsOverRunIsland,
  RELAID_SOURCES,
};
